// -------------------------------
// ESIC BRANCH INTERFACES
// -------------------------------
export interface PendingBranch {
    branch_code?: string;
    branch_name?: string;
    branch_address?: string;
    branch_contact?: string;
}

export interface PendingDeleteBranch {
    awaitingName?: boolean;
}

// -------------------------------
// CREATE BRANCH — START
// -------------------------------
export const startCreateEsicBranchFlow = (
    setPending: (data: PendingBranch | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    nameFromIntent?: string
) => {
    if (nameFromIntent) {
        setPending({ branch_name: nameFromIntent });

        pushAIChat(`What is the branch code for "${nameFromIntent}"?`);
        speak(`What is the branch code for ${nameFromIntent}?`);
        return;
    }

    setPending({});
    pushAIChat("Sure! What should the ESIC branch name be?");
    speak("What should the ESIC branch name be?");
};

// -------------------------------
// CREATE BRANCH — PROCESS
// -------------------------------
export const processPendingEsicBranch = (
    text: string,
    pending: PendingBranch,
    setPending: (data: PendingBranch | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {

    // STEP 1 — Branch Name
    if (!pending.branch_name) {
        const name = text.trim();

        if (!name) {
            pushAIChat("Please provide a valid branch name.");
            speak("Please provide a valid branch name.");
            return;
        }

        setPending({ ...pending, branch_name: name });

        pushAIChat("What is the branch code?");
        speak("What is the branch code?");
        return;
    }

    // STEP 2 — Branch Code
    if (!pending.branch_code) {
        const code = text.trim();

        if (!code) {
            pushAIChat("Please provide a valid branch code.");
            speak("Please provide a valid branch code.");
            return;
        }

        setPending({ ...pending, branch_code: code });

        pushAIChat("What is the branch address?");
        speak("What is the branch address?");
        return;
    }

    // STEP 3 — Branch Address
    if (!pending.branch_address) {
        const address = text.trim();

        if (!address) {
            pushAIChat("Please provide a valid branch address.");
            speak("Please provide a valid branch address.");
            return;
        }

        setPending({ ...pending, branch_address: address });

        pushAIChat("What is the branch contact number?");
        speak("What is the branch contact number?");
        return;
    }

    // STEP 4 — Branch Contact (FINAL)
    if (!pending.branch_contact) {
        const contact = text.trim();

        if (!contact) {
            pushAIChat("Please provide a valid branch contact number.");
            speak("Please provide a valid branch contact number.");
            return;
        }

        // FINAL — CREATE BRANCH
        pushAIChat(`Perfect! Creating ESIC branch "${pending.branch_name}"...`);
        speak(`Creating ESIC branch ${pending.branch_name}.`);

        window.dispatchEvent(
            new CustomEvent("ai-create-esic-branch", {
                detail: {
                    branch_name: pending.branch_name,
                    branch_code: pending.branch_code,
                    branch_address: pending.branch_address,
                    branch_contact: contact,
                }
            })
        );

        setPending(null);
        return;
    }
};

// -------------------------------
// DELETE BRANCH — START
// -------------------------------
export const startDeleteEsicBranchFlow = (
    setPendingDelete: (data: PendingDeleteBranch | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    nameFromIntent?: string
) => {
    if (nameFromIntent) {
        pushAIChat(`Deleting ESIC branch "${nameFromIntent}"...`);
        speak(`Deleting ESIC branch ${nameFromIntent}.`);

        window.dispatchEvent(
            new CustomEvent("ai-delete-esic-branch", {
                detail: { name: nameFromIntent }
            })
        );
        return;
    }

    setPendingDelete({ awaitingName: true });
    pushAIChat("Which ESIC branch would you like to delete?");
    speak("Which ESIC branch would you like to delete?");
};

// -------------------------------
// DELETE BRANCH — PROCESS
// -------------------------------
export const processPendingDeleteEsicBranch = (
    text: string,
    pending: PendingDeleteBranch,
    setPendingDelete: (data: PendingDeleteBranch | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {
    if (!pending.awaitingName) return;

    const cleanName = text.trim();

    if (!cleanName) {
        pushAIChat("Please provide a valid branch name.");
        speak("Please provide a valid branch name.");
        return;
    }

    pushAIChat(`Deleting ESIC branch "${cleanName}"...`);
    speak(`Deleting ESIC branch ${cleanName}.`);

    window.dispatchEvent(
        new CustomEvent("ai-delete-esic-branch", {
            detail: { name: cleanName }
        })
    );

    setPendingDelete(null);
};
